import type { JSX } from 'react'
import type { ICard } from '../Card'

import { useCallback, useMemo, useState } from 'react'
import { BorderOuter } from 'atoms/BorderOuter'
import { Icon, IconNames } from 'atoms/Icon'
import { ImagePoster } from 'atoms/ImagePoster'
import {
  LabelAgeRating,
  LabelContentBadge,
  LabelExternalContent,
  LabelPurchaseBadge,
  LabelVerticalVideo,
} from 'atoms/Label/Templates'
import { OverlayLayout } from 'atoms/OverlayLayout'
import { OverlayLayoutColors } from 'atoms/OverlayLayout/OverlayLayout.h'
import { TextBoxMaxLine1Caption3Secondary } from 'atoms/TextBox/Templates'
import { LabelAclBlockedCard } from 'blocks/AclBlockedLabel'
import { AnimatedWrapper } from 'blocks/AnimatedWrapper'
import { SimpleLabelGroup } from 'blocks/LabelGroup'
import { COLOR } from 'vars/index'
import { CARDS_ASPECT_RATIO, CardCorners } from '../Card.h'
import {
  BaseCardSC,
  CardChildContentSC,
  ImageBackgroundSC,
  LockedPosterSC,
  NewLabelSC,
  PosterContainerSC,
} from './BaseCard.styled'
import { VideoPoster } from './VideoPoster'

export type IBaseCardProps = Omit<
  ICard,
  'transitionName' | 'transitionTrigger' | 'duration'
>

export const BaseCard = ({
  corners = CardCorners.CORNERS_0,
  children,
  posterUrl,
  videoPosterUrl,
  posterHeight,
  posterWidth,
  posterEnabled = true,
  playVideo,
  overlayOnHoverEnabled = true,
  childrenTransition,
  className,
  aspectRatio = CARDS_ASPECT_RATIO.HORIZONTAL_DEFAULT,
  gradient,
  backgroundColor,
  backgroundColorHovered = COLOR.SURFACE_OPACITY_DARK_70,
  overlayEnabled = false,
  overlayHeight = '100%',
  overlayVerticalColor = OverlayLayoutColors.CARD_VERTICAL_OVERLAY,
  overlayGradient,
  hidePriceOnHover: _unusedHidePriceOnHover = true,
  width,
  height,
  cardRef,
  style,
  onClick,
  onMouseEnter,
  onMouseLeave,
  purchaseTitle,
  gridCard = false,
  selected = false,
  borderVisible = false,
  backgroundColorForLogo = COLOR.TRANSPARENT,
  fallbackImageSize,
  fallbackImageBackground = COLOR.TEXT_ICON_CONSTANT_DARK,
  fallbackImageColor = COLOR.TEXT_ICON_TERTIARY,
  roundedPoster,
  locked,
  lockedColor,
  lockedTitle,
  lockedFont,
  lockedSizeIcon,
  handlePosterError,
  handlePosterLoading,
  lazyImage = true,
  rented,
  backgroundCover,
  posterContent,
  lockedIcon = IconNames.pinLock,
  isAgeRating = false,
  ageRatingLabel = '18+',
  transformScale,
  verticalVideo,
  dataTestId,
  paid,
  isExternalContent,
  aclBlockedBy = false,
  detailsCard = false,
  newLabel,
  newLabelShort,
}: IBaseCardProps): JSX.Element => {
  const [childAnimation, setChildAnimation] = useState<boolean>(false)

  const poster = useMemo(
    () =>
      posterUrl
        ? // only for demonstration in storybook.
          aspectRatio > 1
          ? posterUrl
          : posterUrl?.replace('landscape', 'portrait')
        : '',
    [posterUrl, aspectRatio]
  )

  const ConditionRenderImage = useCallback(() => {
    if (gradient) return null

    // FP-4299: pinlocked cards must show the lock overlay even when there is
    // no poster URL (Search / live TV often omit the channel logo).
    if (locked) {
      return (
        <ImageBackgroundSC
          backgroundColorForLogo={backgroundColorForLogo}
          rounded={roundedPoster}
        >
          <LockedPosterSC>
            <Icon name={lockedIcon} size={lockedSizeIcon} color={lockedColor} />
            <TextBoxMaxLine1Caption3Secondary
              title={lockedTitle}
              font={lockedFont}
            />
          </LockedPosterSC>
        </ImageBackgroundSC>
      )
    }

    if (!poster) return null

    return (
      <ImageBackgroundSC
        backgroundColorForLogo={backgroundColorForLogo}
        rounded={roundedPoster}
      >
        <ImagePoster
          posterUrl={poster}
          width={posterWidth}
          height={posterHeight}
          handleLoading={handlePosterLoading}
          handleError={handlePosterError}
          fallbackImageSize={fallbackImageSize}
          fallbackImageBackground={fallbackImageBackground}
          fallbackImageColor={fallbackImageColor}
          lazy={lazyImage}
        />
      </ImageBackgroundSC>
    )
  }, [
    gradient,
    poster,
    locked,
    lazyImage,
    handlePosterError,
    handlePosterLoading,
  ])

  return (
    <BaseCardSC
      className={className!}
      corners={corners}
      overlayOnHoverEnabled={overlayOnHoverEnabled}
      aspectRatio={aspectRatio}
      width={width}
      cardHeight={height}
      onMouseEnter={(event) => {
        setChildAnimation(true)
        onMouseEnter?.(event)
      }}
      onMouseLeave={(event) => {
        setChildAnimation(false)
        onMouseLeave?.(event)
      }}
      onClick={onClick}
      ref={cardRef}
      style={style}
      gridCard={gridCard}
      selected={selected}
      borderVisible={borderVisible}
      gradient={gradient}
      transformScale={transformScale}
      rented={rented}
      data-testid={dataTestId}
      data-details-card={detailsCard || undefined}
      data-acl-blocked={aclBlockedBy || undefined}
    >
      <PosterContainerSC
        overlayOnHoverEnabled={overlayOnHoverEnabled}
        posterHeight={posterHeight}
        posterWidth={posterWidth}
        gradient={gradient}
        locked={locked}
        backgroundColor={backgroundColor}
        corners={corners}
        backgroundColorHovered={backgroundColorHovered}
      >
        {posterUrl! && videoPosterUrl && (
          <VideoPoster
            videoUrl={videoPosterUrl}
            posterHeight={posterHeight}
            posterWidth={posterWidth}
            playVideo={playVideo}
          />
        )}
        {posterEnabled && <ConditionRenderImage />}
        {backgroundCover ? backgroundCover : <></>}
        {overlayEnabled && (
          <OverlayLayout
            overlayEnabled={overlayEnabled}
            overlayHeight={overlayHeight}
            overlayVerticalColor={overlayGradient ?? overlayVerticalColor}
          />
        )}
        {posterContent}
        {!locked && aclBlockedBy ? <LabelAclBlockedCard /> : null}
        {(paid || (rented && purchaseTitle)) && !locked && (
          <BorderOuter>
            <LabelPurchaseBadge title={rented ? purchaseTitle : ''} />
          </BorderOuter>
        )}
        <SimpleLabelGroup>
          {(locked || isAgeRating) && ageRatingLabel ? (
            <LabelAgeRating title={ageRatingLabel} />
          ) : (
            <></>
          )}

          {verticalVideo && !locked && (
            <LabelVerticalVideo data-testid='test-vertical-video-label' />
          )}

          {isExternalContent && <LabelExternalContent />}

          {newLabelShort && !locked && (
            <LabelContentBadge title={newLabelShort} />
          )}
        </SimpleLabelGroup>
        {newLabel && !locked && (
          <NewLabelSC>
            <LabelContentBadge title={newLabel} />
          </NewLabelSC>
        )}

        <CardChildContentSC>
          {childrenTransition && children ? (
            <AnimatedWrapper
              transitionName={childrenTransition}
              transitionTrigger={childAnimation}
            >
              {children}
            </AnimatedWrapper>
          ) : (
            children
          )}
        </CardChildContentSC>
      </PosterContainerSC>
    </BaseCardSC>
  )
}
